import { S, ui } from "../../app/store.js";
import { fmtD, today } from "../../domain/dates.js";
import { can } from "../../services/access.js";
import { inSite, selectedSite, siteName } from "../sites/model.js";
import { esc, peso } from "../../domain/format.js";
import { sitePicker } from "../sites/components.js";
import { reportRows } from "./model.js";
import { csvBtn, panel } from "../../components/ui.js";
import { siteBarChart, trendChart } from "../../components/charts.js";
import { V } from "../../app/pages.js";
import { invoicePartySummary } from "../invoices/identity.js";
import { invoiceParties } from "../../domain/invoicing.js";

function reportingPage(graphs = false) {
  const from = ui.reportFrom || today().slice(0, 4) + "-01-01",
    to = ui.reportTo || today(),
    financial = can("fs.view"),
    payroll = can("pay.view") && (can("hr.salary") || can("pay.approve"));
  const project = ui.reportProject || "ALL",
    projectOptions = S.projects.filter((p) => inSite(p.code));
  const projectFilter = `<label>Project<select id="report-project"><option value="ALL">ALL · All projects</option>${projectOptions.map((p) => `<option value="${esc(p.code)}"${project === p.code ? " selected" : ""}>${esc(p.code)} · ${esc(p.name)}</option>`).join("")}</select></label>`;
  const filters = `<div class="panel"><div class="form">${sitePicker()}${projectFilter}<label>From<input id="report-from" type="date" value="${esc(from)}"></label><label>Through<input id="report-to" type="date" value="${esc(to)}"></label></div><p class="small muted">Posted entries and approved / released payroll only. Outstanding invoices are measured as of the end date.</p></div>`;
  if (from > to)
    return (
      filters +
      '<p class="err">The start date must be on or before the end date.</p>'
    );
  const rows = reportRows(from, to, project).filter(
      (r) => selectedSite() === "ALL" || r.code === selectedSite(),
    ),
    sum = (k) => rows.reduce((a, r) => a + r[k], 0),
    scope =
      selectedSite() === "ALL"
        ? "ALL · Consolidated"
        : siteName(selectedSite());
  const metrics = financial
    ? [
        ["Revenue", sum("revenue")],
        ["Payroll cost", sum("payroll")],
        ["Other expenses", sum("expenses")],
        ["Net result", sum("profit")],
        ["Invoiced · VAT inclusive", sum("invoiced")],
        ["Outstanding invoices", sum("outstanding")],
      ]
    : payroll
      ? [
          ["Net payroll", sum("netPay")],
          ["Payroll cost", sum("payroll")],
        ]
      : [];
  let body =
    filters +
    `<p class="muted">${esc(scope)} · ${fmtD(from)} – ${fmtD(to)}</p><div class="kpis">${metrics.map(([name, n]) => `<div class="kpi"><span class="lbl">${name}</span><span class="v">${peso(n)}</span></div>`).join("")}</div>`;
  if (financial) {
    const cols = [
      ["revenue", "Revenue"],
      ["payroll", "Payroll"],
      ["expenses", "Other expenses"],
      ["totalExpenses", "Total expenses"],
      ["profit", "Net result"],
      ["invoiced", "Invoiced"],
      ["outstanding", "Outstanding"],
    ];
    if (!graphs)
      body += panel(
        "Performance by site / location",
        "Select ALL to include every site and head office.",
        csvBtn("t-site-report"),
        `<div class="tbl"><table id="t-site-report"><thead><tr><th>Location code</th><th>Location</th>${cols.map((x) => `<th class="n">${x[1]}</th>`).join("")}</tr></thead><tbody>${rows.map((r) => `<tr><td><strong>${esc(r.label)}</strong></td><td>${esc(r.location)}</td>${cols.map(([k]) => `<td class="n">${peso(r[k])}</td>`).join("")}</tr>`).join("")}</tbody><tfoot><tr><td colspan="2">${selectedSite() === "ALL" ? "ALL · Consolidated total" : "Location total"}</td>${cols.map(([k]) => `<td class="n">${peso(sum(k))}</td>`).join("")}</tr></tfoot></table></div>`,
      );
    if (!graphs) {
      const invoices = S.invoices.filter(
        (invoice) =>
          invoice.date >= from &&
          invoice.date <= to &&
          inSite(invoice.project) &&
          (project === "ALL" || invoice.project === project),
      );
      const invoiceTotal = (key) =>
        invoices.reduce((total, invoice) => total + invoice[key], 0);
      body += panel(
        "Invoice register",
        "Issued within the selected dates, project and location. Seller and buyer details are the snapshots recorded on each invoice.",
        csvBtn("t-invoice-report"),
        `<div class="tbl"><table id="t-invoice-report"><thead><tr><th>Invoice</th><th>Date</th><th>Project</th><th>Seller · TIN · business address</th><th>Buyer · TIN · business address</th><th class="n">Before VAT</th><th class="n">VAT</th><th class="n">Total</th><th class="n">Payable</th><th>Status</th><th></th></tr></thead><tbody>${
          invoices
            .map((invoice) => {
              const { seller, buyer } = invoiceParties(invoice);
              return `<tr><td>${esc(invoice.no)}</td><td>${fmtD(invoice.date)}</td><td>${esc(invoice.project || "Head office / unassigned")}</td><td>${invoicePartySummary(seller)}</td><td>${invoicePartySummary(buyer)}</td><td class="n">${peso(invoice.net)}</td><td class="n">${peso(invoice.vat)}</td><td class="n">${peso(invoice.gross)}</td><td class="n">${peso(invoice.due)}</td><td>${esc(invoice.status)}</td><td><button type="button" data-act="inv-view" data-id="${esc(invoice.no)}">View</button></td></tr>`;
            })
            .join("") ||
          `<tr><td colspan="11" class="muted">No invoices match these filters.</td></tr>`
        }</tbody><tfoot><tr><td colspan="5">Totals</td><td class="n">${peso(invoiceTotal("net"))}</td><td class="n">${peso(invoiceTotal("vat"))}</td><td class="n">${peso(invoiceTotal("gross"))}</td><td class="n">${peso(invoiceTotal("due"))}</td><td colspan="2"></td></tr></tfoot></table></div>`,
      );
    }
    const months = [];
    let m = from.slice(0, 7);
    while (m <= to.slice(0, 7) && months.length < 120) {
      const end = new Date(Date.UTC(+m.slice(0, 4), +m.slice(5, 7), 0))
          .toISOString()
          .slice(0, 10),
        rs = reportRows(
          from > m + "-01" ? from : m + "-01",
          end < to ? end : to,
          project,
        ).filter((r) => selectedSite() === "ALL" || r.code === selectedSite()),
        get = (k) => rs.reduce((a, r) => a + r[k], 0);
      months.push({
        month: m,
        revenue: get("revenue"),
        expenses: get("totalExpenses"),
        profit: get("profit"),
      });
      const d = new Date(m + "-01T00:00:00Z");
      d.setUTCMonth(d.getUTCMonth() + 1);
      m = d.toISOString().slice(0, 7);
    }
    const max = Math.max(
      1,
      ...months.flatMap((r) => [Math.abs(r.revenue), Math.abs(r.expenses)]),
    );
    if (!graphs)
      body += panel(
        "Monthly revenue & expenses",
        "Revenue and expenses by posting date. Historical sample activity is recorded at 30 September 2026.",
        csvBtn("t-month-report"),
        `<div class="tbl"><table id="t-month-report"><thead><tr><th>Month</th><th>Revenue / expenses</th><th class="n">Revenue</th><th class="n">Expenses</th><th class="n">Net result</th></tr></thead><tbody>${months.map((r) => `<tr><td>${esc(r.month)}</td><td style="min-width:140px"><div class="bar" title="Revenue"><span style="width:${(Math.abs(r.revenue) / max) * 100}%"></span></div><div class="bar" title="Expenses" style="margin-top:5px"><span style="width:${(Math.abs(r.expenses) / max) * 100}%;background:var(--muted,#666)"></span></div></td>${["revenue", "expenses", "profit"].map((k) => `<td class="n">${peso(r[k])}</td>`).join("")}</tr>`).join("")}</tbody></table></div>${months.length === 120 ? '<p class="small muted">Monthly trend shows the first 120 months. Narrow the dates to see later months.</p>' : ""}`,
      );

    if (graphs) {
      body += panel(
        "Monthly revenue & expenses",
        "Revenue and expenses by posting date. Historical sample activity is recorded at 30 September 2026.",
        "",
        trendChart(months, [
          { key: "revenue", label: "Revenue", color: "#092f94" },
          { key: "expenses", label: "Expenses", color: "#c57534" },
        ]) +
          (months.length === 120
            ? '<p class="small muted">Showing the first 120 months. Narrow the date range for later months.</p>'
            : ""),
      );
      body += panel(
        "Revenue & costs by location",
        "Compare revenue, payroll and other expenses in the selected period.",
        "",
        siteBarChart(rows, [
          { key: "revenue", label: "Revenue", color: "#092f94" },
          { key: "payroll", label: "Payroll", color: "#777777" },
          { key: "expenses", label: "Other expenses", color: "#c57534" },
        ]),
      );
      body += panel(
        "Net result by location",
        "Revenue less payroll and other posted expenses. Negative values extend left of zero.",
        "",
        siteBarChart(rows, [
          { key: "profit", label: "Net result", color: "#092f94" },
        ]),
      );
      body += panel(
        "Outstanding invoices by location",
        `Amounts still unpaid as of ${fmtD(to)}.`,
        "",
        siteBarChart(rows, [
          { key: "outstanding", label: "Outstanding", color: "#c57534" },
        ]),
      );
    }
  }
  if (payroll && !graphs)
    body += panel(
      "Payroll report by location",
      "Pay date within the selected period; drafts excluded. People working across sites appear in each applicable site.",
      csvBtn("t-pay-report"),
      `<div class="tbl"><table id="t-pay-report"><thead><tr><th>Location</th><th class="n">Employees</th><th class="n">Net payroll</th></tr></thead><tbody>${rows.map((r) => `<tr><td>${esc(r.label)}</td><td class="n">${r.employees.size}</td><td class="n">${peso(r.netPay)}</td></tr>`).join("")}</tbody><tfoot><tr><td>Total · unique employees</td><td class="n">${new Set(rows.flatMap((r) => [...r.employees])).size}</td><td class="n">${peso(sum("netPay"))}</td></tr></tfoot></table></div>`,
    );
  if (payroll && graphs)
    body += panel(
      "Net payroll by location",
      "Approved and released runs by pay date; drafts excluded.",
      "",
      siteBarChart(rows, [
        { key: "netPay", label: "Net payroll", color: "#092f94" },
      ]),
    );
  return body;
}

V.reports = () => reportingPage(false);

V.analytics = () => reportingPage(true);

export { reportingPage };
